import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import {
	type CommentFormModel,
	getCommentFormSchema,
} from "Shared/Components/Comments/Form/Schema/commentSchema";
import TextEditor from "Shared/Components/Form/CustomFormComponent/TextEditor";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Stack } from "@mui/material";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";

export type Props = {
	onSubmit: (data: CommentFormModel) => void;
	isPending: boolean;
	handleCancel?: () => void;
	message?: string;
	isUpdate?: boolean;
};

const commentResources = Resources.base.order.detail.commentForm;

export const CommentForm: FC<Props> = ({
	onSubmit,
	isPending,
	message,
	handleCancel,
	isUpdate,
}) => {
	const { t } = useResource();

	const form = useForm<CommentFormModel>({
		resolver: zodResolver(getCommentFormSchema()),
		defaultValues: { message: message ?? "" },
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitComment = () => {
		handleServerError(onSubmit, {
			onSuccess: () => form.reset({ message: "" }),
		});
	};

	useEffect(() => {
		if (message) {
			form.setValue("message", message);
		}
	}, [message, form]);

	return (
		<Box p={1}>
			<TextEditor name="message" control={control} errors={errors} />
			<Stack direction="row" spacing={1} mt={2} justifyContent="flex-end">
				{handleCancel && (
					<Button variant="outlined" color="primary" onClick={handleCancel}>
						{t(commentResources.cancel)}
					</Button>
				)}
				<Button
					variant="contained"
					color="primary"
					onClick={handleSubmitComment}
					loading={isPending}
				>
					{isUpdate
						? t(commentResources.updateComment)
						: t(commentResources.addComment)}
				</Button>
			</Stack>
		</Box>
	);
};
